import { useCallback, useEffect, useState } from "react";

import * as api from "@/api/client";
import type { BrandHealth } from "@/types/api";

/**
 * Polls the Vigil brands endpoint every 60s. Analogous to
 * useVigilSnapshotPoll — same pattern, isolated state. Ported from
 * products/x2mail-admin/src/hooks/useBrandsPoll.ts.
 */
const BRANDS_INTERVAL_MS = 60_000;

export interface BrandsPollState {
  brands: BrandHealth[];
  loading: boolean;
  error: string | null;
  fetchedAt: string | null;
}

export function useVigilBrandsPoll(): BrandsPollState {
  const [brands, setBrands] = useState<BrandHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getVigilBrands();
      setBrands(res.brands);
      setFetchedAt(res.fetchedAt);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd pobierania portfela marek");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    }

    void init();

    const timer = setInterval(() => {
      void load();
    }, BRANDS_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [load]);

  return { brands, loading, error, fetchedAt };
}
